import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useDismiss } from "./Dismiss";
import { useModalKeys } from "./Modal";

// A button that opens a dialog beside it, part of the page and not modal:
// a row's Resume on /shares, a share's Invite (byj-1wv.51.3). Open, the focus
// goes to what `focus` names inside it; Esc gives it back to the button, as
// a second press on the button does in every browser (useDismiss's
// keepFocus), and a press outside — or, with `closeOnFocusOut`, the focus
// leaving — closes it. What it shows is given `close`, which gives the focus
// back too.

export function Popover({
  label,
  button,
  buttonLabel,
  buttonClassName,
  buttonData,
  className,
  popClassName,
  focus,
  closeOnFocusOut = false,
  disabled = false,
  onOpen,
  children,
}: {
  /** the dialog's name */
  label: string;
  /** what the button shows */
  button: ReactNode;
  /** the button's name, when what it shows does not say it */
  buttonLabel?: string;
  buttonClassName?: string;
  /** `data-*` attributes on the button, for a test or a driver to read */
  buttonData?: Record<`data-${string}`, string>;
  /** the wrapper's class: what the dialog is placed against */
  className?: string;
  popClassName: string;
  /** a selector inside the dialog for what takes the focus as it opens */
  focus: string;
  closeOnFocusOut?: boolean;
  /** the button off, and what it opened closed: nothing left for it to reach */
  disabled?: boolean;
  /** told each time it opens: what it shows read again, say */
  onOpen?: () => void;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = (back: boolean) => {
    setOpen(false);
    if (back) trigger.current?.focus();
  };
  // Esc, and a second press on the button, give the button the focus back
  const dismiss = useDismiss(open, (how) => close(how === "escape" || how === "opener"), [wrap], { onFocusOut: closeOnFocusOut });
  // open, it is the top of the dialogs' stack (components/Modal), as
  // PersonField's list is: one opened inside a dialog — Give to a class in a
  // file's Share — takes Esc alone, and the dialog under it stays
  // (byj-1wv.51.17). Its Tab is not kept in it: it is not modal.
  useModalKeys(() => close(true), open && !disabled);

  useEffect(() => {
    if (open) pop.current?.querySelector<HTMLElement>(focus)?.focus();
  }, [open, focus]);

  return (
    <span ref={wrap} className={className} onBlur={dismiss.onBlur}>
      <button
        ref={trigger}
        type="button"
        className={buttonClassName}
        aria-label={buttonLabel}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        // Safari and Firefox do not focus a pressed button, so its press
        // would read as the focus leaving: keepFocus stops that, and the
        // click itself closes what it opened (useDismiss)
        onMouseDown={dismiss.keepFocus}
        onClick={() => {
          if (open) return close(true);
          setOpen(true);
          onOpen?.();
        }}
        {...buttonData}
      >
        {button}
      </button>
      {open && !disabled && (
        <div ref={pop} id={id} className={popClassName} role="dialog" aria-label={label} onKeyDown={dismiss.onKeyDown}>
          {children(() => close(true))}
        </div>
      )}
    </span>
  );
}
